/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from 'vue'
import Router from 'vue-router'
import auth from '@/auth/authService'
import store from '@/store/store'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/dashboard/overview',
          meta: {
            authRequired: true,
          },
        },
        {
          path: '/dashboard/overview',
          name: 'dashboard-overview',
          component: () => import('./views/AccountDashboard.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Dashboard', active: true },
            ],
            pageTitle: 'Account Dashboard',
            rule: 'editor',
          },
        },
        {
          path: '/tunnels/list',
          name: 'tunnel-list',
          component: () =>
            import('@/views/sentry-overview/SentryOverview.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Tunnels' },
              { title: 'Overview', active: true },
            ],
            pageTitle: 'Tunnels Overview',
            rule: 'editor',
          },
        },
        {
          path: '/tunnels/:tunnel_id/edit',
          name: 'tunnel-analytics',
          props: true,
          component: () =>
            import('@/views/sentry-overview/SentryEdit.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Tunnels', url: '/tunnels/list' },
              { title: 'Overview and Analytics', active: true },
            ],
            pageTitle: 'Tunnels Overview',
            rule: 'editor',
          },
        },
        {
          path: '/networks/overview',
          name: 'network-overview',
          component: () =>
            import('./views/network-overview/NetworkOverview.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Networks' },
              { title: 'Overview', active: true },
            ],
            pageTitle: 'Network Overview',
            rule: 'editor',
          },
        },
        {
          path: '/networks/manage',
          name: 'network-management',
          component: () =>
            import('./views/network-managment/NetworkManagement.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Globe', url: '/' },
              { title: 'Networks' },
              { title: 'Domains', active: true },
            ],
            pageTitle: 'Domain Management',
            rule: 'editor',
          },
        },
        {
          path: '/billing/overview',
          name: 'billing-overview',
          component: () => import('./views/billing-area/Overview.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Billing' },
              { title: 'Overview', active: true },
            ],
            pageTitle: 'Billing Overview',
            rule: 'editor',
          },
        },

        {
          path: '/networks/backends',
          name: 'backend-sets',
          component: () => import('./views/backend-sets/Overview.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Networks' },
              { title: 'Backend Sets', active: true },
            ],
            pageTitle: 'Backend Sets',
            rule: 'editor',
          },
        },
        {
          path: '/networks/sub-users',
          name: 'sub-users',
          component: () => import('./views/sub-users/Main.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Networks' },
              { title: 'Sub Users', active: true },
            ],
            pageTitle: 'Sub Users',
            rule: 'editor',
          },
        },
        {
          path: '/networks/bedrock',
          name: 'bedrock',
          component: () => import('./views/bedrock/Main.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Networks' },
              { title: 'Bedrock', active: true },
            ],
            pageTitle: 'Bedrock',
            rule: 'editor',
          },
        },
        {
          path: '/networks/mitigation-settings',
          name: 'mitigation-settings',
          component: () => import('./views/mitigation-settings/Overview.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Networks' },
              { title: 'Mitigation Settings', active: true },
            ],
            pageTitle: 'Mitigation Settings',
            rule: 'editor',
          },
        },
        {
          path: '/networks/attack-history',
          name: 'attack-history',
          component: () => import('./views/attacks/Overview.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Networks' },
              { title: 'Attack History', active: true },
            ],
            pageTitle: 'Attack History',
            rule: 'editor',
          },
        },
        {
          path: '/networks/analytics',
          name: 'network-analytics',
          component: () => import('./views/analytics/Main.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Networks' },
              { title: 'Analytics', active: true },
            ],
            pageTitle: 'Analytics',
            rule: 'editor',
          },
        },

        // {
        //   path: '/alerts',
        //   name: 'alerts',
        //   component: () => import('./views/alerts/Overview.vue'),
        //   meta: {
        //     authRequired: true,
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Alerts', active: true },
        //     ],
        //     pageTitle: 'Alerts',
        //     rule: 'editor',
        //   },
        // },

        {
          path: '/billing/plans',
          name: 'billing-plans',
          component: () => import('./views/billing-area/plans.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Billing' },
              { title: 'Plans', active: true },
            ],
            pageTitle: 'Billing Plans',
            rule: 'editor',
          },
        },
        // =============================================================================
        // Pages Routes
        // =============================================================================
        // {
        //   path: '/pages/profile',
        //   name: 'page-profile',
        //   component: () => import('@/views/pages/Profile.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Pages' },
        //       { title: 'Profile', active: true },
        //     ],
        //     pageTitle: 'Profile',
        //     rule: 'editor',
        //   },
        // },
        {
          path: '/pages/profile',
          name: 'page-user-profile',
          component: () =>
            import('@/views/pages/user-settings/UserSettings.vue'),
          meta: {
            authRequired: true,
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'User Settings', active: true },
            ],
            pageTitle: 'Settings',
            rule: 'editor',
          },
        },
        {
          path: '/pages/documentation',
          name: 'page-documentation',
          component: () => import('@/views/pages/Documentation.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'Setup Guide', active: true },
            ],
            pageTitle: 'Documentation',
            rule: 'editor',
          },
          beforeEnter: () => window.open('https://docs.tcpshield.com', '_blank') // location.href = 'https://docs.tcpshield.com'
        },
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/login',
          name: 'page-login',
          component: () => import('@/views/pages/login/Login.vue'),
          meta: {
            rule: 'editor',
          },
        },
        {
          path: '/register',
          name: 'page-register',
          component: () => import('@/views/pages/register/Register.vue'),
          meta: {
            rule: 'editor',
          },
        },
        {
          path: '/activate-account/:token',
          name: 'page-activate-account',
          props: true,
          component: () => import('@/views/pages/ActivateAccount.vue'),
          meta: {
            rule: 'editor',
          },
        },
        {
          ///networks/sub-users/swainsd'sdnetwork/1/2/verify
          path: '/networks/sub-users/:name/:network_id/:user_id/verify',
          name: 'page-activate-sub-account',
          props: true,
          component: () => import('@/views/pages/ActivateSubAccount.vue'),
          meta: {
            authRequired: true,
            rule: 'editor',
          },
        },
        {
          path: '/reset-password/:token',
          name: 'page-reset-password',
          props: true,
          component: () => import('@/views/pages/ResetPassword.vue'),
          meta: {
            rule: 'editor',
          },
        },
        {
          path: '/forgot-password',
          name: 'page-forgot-password',
          component: () => import('@/views/pages/ForgotPassword.vue'),
          meta: {
            rule: 'editor',
          },
        },
        {
          path: '/pages/lock-screen',
          name: 'page-lock-screen',
          component: () => import('@/views/pages/LockScreen.vue'),
          meta: {
            rule: 'editor',
          },
        },
        {
          path: '/pages/comingsoon',
          name: 'page-coming-soon',
          component: () => import('@/views/pages/ComingSoon.vue'),
          meta: {
            rule: 'editor',
          },
        },
        {
          path: '/pages/404',
          name: 'page-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            rule: 'editor',
          },
        },
        {
          path: '/pages/error-500',
          name: 'page-error-500',
          component: () => import('@/views/pages/Error500.vue'),
          meta: {
            rule: 'editor',
          },
        },
        {
          path: '/pages/not-authorized',
          name: 'page-not-authorized',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            rule: 'editor',
          },
        },
      ],
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/404',
    },
  ],
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

router.beforeEach((to, from, next) => {
  // if (
  //     to.path === "/pages/login" ||
  //     to.path === "/pages/forgot-password" ||
  //     to.path === "/pages/error-404" ||
  //     to.path === "/pages/error-500" ||
  //     to.path === "/pages/register" ||
  //     to.path === "/callback" ||
  //     to.path === "/pages/comingsoon" ||
  //     auth.isAuthenticated())
  // ) {
  //     return next();
  // }

  // If auth required, check login. If login fails redirect to login page
  if (to.meta.authRequired) {
    if (!auth.isAuthenticated() && !auth.validRefreshToken()) {
      auth.purgeStorage()
      return router.push({ path: '/login', query: { to: to.path } }).catch(err => {})
    }
  }

   return next()

  // Specify the current path as the customState parameter, meaning it
  // will be returned to the application after auth
  // auth.login({ target: to.path });
})

export default router
